import { ref } from "vue";
import { defineStore } from "pinia";
import type { SocketEventRecordType, SocketEventSourceType } from "@/types/socket";

/**
 * Bus degli eventi socket: un solo componente tiene la connessione (oggi
 * AppSocketListener.vue, montato in App.vue) e ripubblica qui quello che riceve, così chiunque
 * nell'applicazione può reagire senza aprire un canale proprio.
 *
 * Non duplica stato di dominio: tiene l'ultimo evento per sorgente, niente altro. Le liste, i
 * contatori e il resto restano dove già sono, e si ricaricano dal backend quando serve.
 */
export const useSocketEvents = defineStore("socketEvents", () => {
  /** Ultimo evento ricevuto per ciascuna sorgente: è la voce che i consumatori osservano */
  const lastEventBySource = ref<
    Partial<Record<SocketEventSourceType, SocketEventRecordType>>
  >({});

  /** Ultimo evento in assoluto, a prescindere dalla sorgente: utile per diagnostica e log */
  const lastEvent = ref<SocketEventRecordType | null>(null);

  let sequence = 0;

  function publish<T>(source: SocketEventSourceType, payload: T): void {
    if (!source) throw new Error("useSocketEvents.publish: source is required");

    sequence += 1;
    const record: SocketEventRecordType<T> = {
      source,
      payload,
      sequence,
      receivedAt: new Date().toISOString(),
    };

    // nuovo oggetto a ogni evento: due messaggi identici nel contenuto restano due notifiche
    lastEventBySource.value = { ...lastEventBySource.value, [source]: record };
    lastEvent.value = record;
  }

  return { lastEventBySource, lastEvent, publish };
});
